<template>
    <div class="ui-page">
        <ui-page-header :title="item ? item.company_name : 'Carregando...'" :back="backTo" :back-text="backText">
            <template #title-append>
                <template v-if="item">
                    <ui-badge set="prospect-type" :status="item.type" />
                    <ui-badge set="prospect-pipeline" :status="item.pipeline_status" />
                </template>
            </template>
            <template #subtitle>
                <span v-if="item" class="ui-mono">{{ item.process_number }}</span>
                <span v-if="item"> · {{ item.process_class || item.type_label }} · {{ [item.tribunal, item.system].filter(Boolean).join(" · ") || "tribunal não informado" }}
                    · ajuizada em {{ formatFeedDate(item.filed_at) }}</span>
            </template>
            <template v-if="item">
                <v-btn outlined :disabled="!item.files_available" @click="openFiles">
                    <v-icon left small>mdi-folder-download-outline</v-icon>{{ item.files_stored ? "Íntegra" : "Íntegra (fornecedor)" }}
                </v-btn>
                <v-btn outlined @click="openProcedural"><v-icon left small>mdi-text-search</v-icon>Conferir status</v-btn>
                <v-menu offset-y left>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn outlined v-bind="attrs" v-on="on"><v-icon left small>mdi-swap-horizontal</v-icon>Mudar etapa</v-btn>
                    </template>
                    <v-list dense>
                        <v-list-item v-for="option in statusItems" :key="option.value" :disabled="option.value === item.pipeline_status" @click="changeStatus(option.value)">
                            <v-list-item-title>{{ option.text }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
                <v-btn v-if="$allowed('edit')" outlined @click="meetingDialog = true"><v-icon left small>mdi-calendar-plus</v-icon>Marcar agenda</v-btn>
                <v-btn v-if="$allowed('create')" color="primary" depressed @click="openActivity()"><v-icon left small>mdi-plus</v-icon>Registrar andamento</v-btn>
            </template>
        </ui-page-header>

        <template v-if="item">
            <section class="pd-facts mb-5" aria-label="Resumo do caso">
                <div class="pd-fact">
                    <span class="pd-fact__label">Passivo <v-btn v-if="$allowed('edit')" x-small icon aria-label="Editar dados" @click="openEdit"><v-icon x-small>mdi-pencil-outline</v-icon></v-btn></span>
                    <span class="pd-fact__value">{{ formatMoney(item.amount) }}</span>
                    <span class="pd-fact__hint">{{ item.amount_source === "passivo" ? "passivo declarado" : (item.amount ? "valor da causa (lançar o passivo)" : "não informado") }}<span v-if="item.below_threshold"> · abaixo do corte</span></span>
                </div>
                <div class="pd-fact">
                    <span class="pd-fact__label">Setor</span>
                    <span class="pd-fact__value pd-fact__value--sm">{{ item.sector_label || "—" }}</span>
                    <span class="pd-fact__hint">{{ item.uf || "—" }} · {{ item.city || item.comarca || "—" }}</span>
                </div>
                <div class="pd-fact">
                    <span class="pd-fact__label">Dono do caso</span>
                    <v-select :value="item.owner ? item.owner.id : null" :items="ownerItems" dense hide-details placeholder="Sem dono" class="pd-owner" :menu-props="{ offsetY: true }"
                        :disabled="busy" aria-label="Dono do caso" @change="setOwner"></v-select>
                    <span class="pd-fact__hint">{{ item.owner_assigned_at ? `desde ${formatDate(item.owner_assigned_at)}` : "atribua para entrar na régua" }}</span>
                </div>
                <div class="pd-fact">
                    <span class="pd-fact__label">Nomeação PP · AJ</span>
                    <span class="pd-fact__value pd-fact__value--sm">
                        <button type="button" class="pd-link" @click="openAppointment('pp')">PP: {{ appointmentText(item.pp_status, item.pp_administrator) }}</button>
                        <button type="button" class="pd-link" @click="openAppointment('aj')">AJ: {{ appointmentText(item.aj_status, item.aj_administrator) }}</button>
                    </span>
                    <span class="pd-fact__hint">{{ item.lost_reason ? `motivo: ${item.lost_reason}` : "clique para lançar a nomeação" }}</span>
                </div>
            </section>

            <div v-if="item.needs_review" class="pd-notice mb-4">
                <v-icon small>mdi-information-outline</v-icon>
                <span>Entrou pela revisão da importação (classe "{{ item.process_class }}"). Confirme se é mesmo {{ item.type_label }} e ajuste o tipo em "Editar dados" se preciso.</span>
            </div>
            <div v-if="item.pipeline_status === 'nao_atuar' && item.no_action_reason" class="pd-notice pd-notice--neutral mb-4">
                <v-icon small>mdi-cancel</v-icon><span>Optamos por não atuar: {{ item.no_action_reason }}</span>
            </div>

            <div class="pd-grid">
                <div class="pd-main">
                    <ui-section title="Linha do tempo" description="Movimentos da BeJ, dos autos e decisões internas, do mais recente para o mais antigo.">
                        <ui-empty v-if="!item.activities.length" text="Nenhum andamento registrado." />
                        <ol v-else class="pd-timeline">
                            <li v-for="activity in item.activities" :key="activity.id" class="pd-event" :class="{ 'pd-event--auto': activity.automatic }">
                                <div class="pd-event__date">{{ formatDay(activity.occurred_at) }}</div>
                                <div class="pd-event__body">
                                    <div class="pd-event__head">
                                        <span class="pd-event__type" :class="`pd-event__type--${tone(activity.type)}`">{{ activity.type_label }}</span>
                                        <span v-if="activity.admin" class="ui-small ui-muted">{{ activity.admin.name }}</span>
                                        <span v-if="activity.with_whom" class="ui-small ui-muted">· com {{ activity.with_whom }}</span>
                                        <v-spacer></v-spacer>
                                        <v-btn v-if="(!activity.automatic) && $allowed('delete')" x-small icon aria-label="Excluir andamento" @click="removeActivity(activity)"><v-icon x-small>mdi-delete-outline</v-icon></v-btn>
                                    </div>
                                    <div class="pd-event__text">{{ activity.description }}</div>
                                    <div v-if="activity.feedback" class="pd-event__feedback"><strong>Feedback do juízo:</strong> {{ activity.feedback }}</div>
                                    <div v-if="activity.next_step" class="ui-small ui-muted">Próximo passo: {{ activity.next_step }}<span v-if="activity.next_step_at"> · {{ formatDate(activity.next_step_at) }}</span></div>
                                </div>
                            </li>
                        </ol>
                    </ui-section>

                    <ui-section title="Agendas e materiais" class="mt-4">
                        <template #actions><v-btn v-if="$allowed('edit')" small text @click="meetingDialog = true">Marcar agenda</v-btn></template>
                        <ui-empty v-if="!item.meetings || !item.meetings.length" text="Nenhuma agenda com este caso." />
                        <table v-else class="pd-meetings">
                            <thead><tr><th>Data</th><th>Modalidade</th><th>Com quem</th><th>Responsáveis</th><th>Onepage</th><th>Resultado</th></tr></thead>
                            <tbody>
                                <tr v-for="meeting in item.meetings" :key="meeting.id">
                                    <td class="ui-nowrap">{{ formatFeedDate(meeting.date) }} {{ meeting.time }}</td>
                                    <td>{{ meeting.mode_label }}</td>
                                    <td>{{ meeting.with_whom || (meeting.judge ? meeting.judge.name : "—") }}</td>
                                    <td>{{ meeting.attendees.map((person) => person.name).join(", ") }}</td>
                                    <td>{{ meeting.onepage_label }}</td>
                                    <td>
                                        <ui-badge set="prospect-meeting" :status="meeting.status" />
                                        <div v-if="meeting.result_label" class="ui-small">{{ meeting.result_label }}</div>
                                        <div v-if="meeting.feedback" class="ui-small ui-muted">{{ meeting.feedback }}</div>
                                        <router-link v-if="meeting.trip" :to="`/prospeccao/rota/${meeting.trip.id}`" class="ui-small pd-trip-link"><v-icon x-small>mdi-map-marker-path</v-icon>{{ meeting.trip.name }}</router-link>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <p class="ui-small ui-muted mt-2 mb-0">Para registrar o resultado, abra a agenda em <router-link to="/prospeccao/agendas">Agenda de visitas</router-link>.</p>
                    </ui-section>

                    <ui-section title="Roteiros de visita" class="mt-4" description="Viagens em que o caso entra. A agenda presencial sai do roteiro em “Gerar agendas”.">
                        <template #actions><v-btn v-if="$allowed('create')" small text @click="tripPicker = true"><v-icon left small>mdi-map-marker-plus-outline</v-icon>Incluir em roteiro</v-btn></template>
                        <ui-empty v-if="!trips.length" text="O caso não está em nenhum roteiro." />
                        <div v-for="trip in trips" :key="trip.id" class="pd-trip" :class="{ 'pd-trip--closed': !trip.open }">
                            <i class="pd-trip__dot" :style="{ background: trip.team ? trip.team.color : '#94a3b8' }"></i>
                            <div class="pd-trip__main">
                                <router-link :to="`/prospeccao/rota/${trip.id}`"><strong>{{ trip.name }}</strong></router-link>
                                <div class="ui-small ui-muted">
                                    <span v-if="trip.team">{{ trip.team.name }}</span>
                                    <span v-if="trip.start_date"> · saída {{ formatFeedDate(trip.start_date) }}</span>
                                    <span v-if="trip.pin_day"> · dia {{ trip.pin_day }} combinado</span><span v-if="trip.pin_time"> às {{ trip.pin_time }}</span>
                                    <span v-if="trip.meeting"> · agenda {{ formatFeedDate(trip.meeting.scheduled_at.slice(0, 10)) }} {{ trip.meeting.scheduled_at.slice(11, 16) }}</span>
                                    <span v-else-if="trip.open"> · agenda ainda não gerada</span>
                                </div>
                            </div>
                            <ui-badge set="prospect-trip" :status="trip.status" />
                        </div>
                    </ui-section>
                </div>

                <div class="pd-side">
                    <ui-section v-if="item.type === 'tca' || item.deadline" title="Prazo da TCA" class="mb-4">
                        <template #actions>
                            <v-btn v-if="$allowed('create')" small text @click="openDeadline"><v-icon left small>mdi-calendar-edit</v-icon>{{ item.deadline ? "Ajustar" : "Lançar" }}</v-btn>
                        </template>
                        <template v-if="item.deadline">
                            <div v-if="item.deadline.rj_candidate_case_id && item.deadline.open" class="pd-notice mb-3">
                                <v-icon small>mdi-link-variant</v-icon>
                                <span>Possível RJ distribuída com o mesmo CNPJ.
                                    <router-link :to="`/prospeccao/casos/${item.deadline.rj_candidate_case_id}`">Ver caso</router-link> e registre o desfecho.</span>
                            </div>
                            <div class="pd-due" :class="`pd-due--${item.deadline.tone}`">
                                <span class="pd-due__date">{{ formatFeedDate(item.deadline.due_date) }}</span>
                                <span v-if="item.deadline.open">{{ item.deadline.days_text }}<span v-if="item.deadline.estimated"> · estimado</span></span>
                                <span v-else>{{ item.deadline.outcome_label }}<span v-if="item.deadline.rj_process_number"> · {{ item.deadline.rj_process_number }}</span></span>
                            </div>
                            <dl class="pd-dl mt-3">
                                <dt>Base</dt>
                                <dd>{{ item.deadline.days }} dias corridos da {{ item.deadline.base_label }}<span v-if="item.deadline.base_date"> ({{ formatFeedDate(item.deadline.base_date) }})</span></dd>
                                <template v-if="item.deadline.extensions.length">
                                    <dt>Prorrogações</dt>
                                    <dd><div v-for="(extension, index) in item.deadline.extensions" :key="index">{{ formatFeedDate(extension.from) }} → {{ formatFeedDate(extension.to) }}<span v-if="extension.note" class="ui-muted"> · {{ extension.note }}</span></div></dd>
                                </template>
                                <template v-if="item.deadline.open">
                                    <dt>Próximo envio</dt>
                                    <dd>
                                        <span v-if="item.deadline.reminders_paused" class="ui-muted">lembretes pausados</span>
                                        <span v-else-if="item.deadline.next_milestone">{{ item.deadline.next_milestone.label }} · {{ formatFeedDate(item.deadline.next_milestone.date) }}</span>
                                        <span v-else class="ui-muted">fim da régua</span>
                                    </dd>
                                </template>
                                <dt>Lembretes</dt>
                                <dd>
                                    <span v-if="!item.deadline.reminders.length" class="ui-muted">nenhum enviado</span>
                                    <div v-for="reminder in item.deadline.reminders" :key="reminder.id">
                                        {{ reminder.milestone_label }} · {{ reminder.sent_at ? formatDateTime(reminder.sent_at) : "aguardando" }}
                                        <span :class="reminder.reply ? '' : 'ui-muted'"> · {{ reminder.reply_label || "sem resposta" }}</span>
                                    </div>
                                </dd>
                            </dl>
                            <div class="pd-actions mt-3">
                                <template v-if="item.deadline.open">
                                    <v-btn x-small outlined @click="openExtension">Prorrogar</v-btn>
                                    <v-btn v-if="$allowed('create')" x-small outlined @click="openOutcome">Registrar desfecho</v-btn>
                                    <v-btn v-if="$allowed('edit')" x-small outlined :disabled="busy" @click="togglePause">{{ item.deadline.reminders_paused ? "Retomar lembretes" : "Pausar lembretes" }}</v-btn>
                                    <v-btn x-small text @click="openPreview">Prévia do e-mail</v-btn>
                                </template>
                                <v-btn v-else-if="$allowed('edit')" x-small outlined :disabled="busy" @click="reopenDeadline">Reabrir prazo</v-btn>
                            </div>
                        </template>
                        <p v-else class="ui-muted mb-0">O prazo nasce quando a TCA entra em acompanhamento. Você também pode lançá-lo agora.</p>
                    </ui-section>

                    <ui-section title="Juízo e contatos">
                        <template #actions>
                            <v-btn v-if="(item.court) && $allowed('edit')" small text @click="openCourt"><v-icon left small>mdi-pencil-outline</v-icon>Editar</v-btn>
                        </template>
                        <dl class="pd-dl">
                            <dt>Juiz(a)</dt><dd>{{ item.judge_name || "não informado" }}</dd>
                            <dt>Vara</dt><dd>{{ item.court_name || "—" }}</dd>
                            <template v-if="item.court">
                                <dt>Assessor(a)</dt><dd :class="{ 'ui-muted': !item.court.clerk_name }">{{ item.court.clerk_name || "a preencher" }}</dd>
                                <dt>Telefone</dt><dd :class="{ 'ui-muted': !item.court.phone }">{{ item.court.phone || "a preencher" }}</dd>
                                <dt>E-mail</dt><dd :class="{ 'ui-muted': !item.court.email }">{{ item.court.email || "a preencher" }}</dd>
                                <dt v-if="item.court.notes">Observações</dt><dd v-if="item.court.notes">{{ item.court.notes }}</dd>
                            </template>
                            <dt>Histórico da vara</dt>
                            <dd>
                                <span v-if="!item.court_cases.length" class="ui-muted">nenhum outro caso captado nesta vara</span>
                                <div v-for="other in item.court_cases" :key="other.id">
                                    <router-link :to="`/prospeccao/casos/${other.id}`">{{ other.company_name }}</router-link>
                                    <span class="ui-muted"> · {{ other.aj_administrator ? `AJ: ${other.aj_administrator.name}` : pipelineLabel(other.pipeline_status) }}</span>
                                </div>
                            </dd>
                        </dl>
                    </ui-section>

                    <ui-section title="Status processual" class="mt-4">
                        <template #actions><v-btn small text @click="openProcedural">Conferir</v-btn></template>
                        <p v-if="item.procedural_status" class="mb-1">{{ item.procedural_status }}</p>
                        <p v-else class="ui-muted mb-1">Ainda não conferido.</p>
                        <p v-if="item.procedural_checked_at" class="ui-small ui-muted mb-0">Última conferência: {{ formatDateTime(item.procedural_checked_at) }}<span v-if="item.procedural_checked_by"> · {{ item.procedural_checked_by.name }}</span></p>
                    </ui-section>

                    <ui-section title="Dados do processo" class="mt-4">
                        <dl class="pd-dl">
                            <dt>Valor da causa</dt><dd>{{ formatMoney(item.claim_amount) }}</dd>
                            <dt>CNPJ</dt><dd>{{ item.company_document ? formatDocument(item.company_document) : "—" }}</dd>
                            <dt>Autores</dt><dd>{{ partiesText(item.authors) || "—" }}</dd>
                            <dt v-if="item.defendants.length">Réus</dt><dd v-if="item.defendants.length">{{ partiesText(item.defendants) }}</dd>
                            <dt>Advogado do devedor</dt><dd>{{ item.authors_lawyers || "—" }}</dd>
                            <dt>Fonte</dt><dd>{{ item.source_label }} · captado em {{ formatFeedDate(item.captured_at) }}</dd>
                            <dt v-if="item.summary_ai">Resumo do fornecedor</dt><dd v-if="item.summary_ai" class="ui-small">{{ item.summary_ai }}</dd>
                        </dl>
                    </ui-section>
                </div>
            </div>
        </template>

        <prospect-meeting-dialog v-model="meetingDialog" :initial-cases="item ? [item] : []" @saved="load" />
        <prospect-trip-picker v-model="tripPicker" :case-ids="item ? [item.id] : []" :uf="item ? item.uf : null" @done="loadTrips" />

        <!-- Novo andamento -->
        <v-dialog v-model="activity.open" max-width="640" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Novo andamento</h3>
                        <p class="ui-section-description">Aparece na linha do tempo e nas colunas de último movimento da lista de pendentes.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-row dense>
                        <v-col cols="12" sm="7"><v-select v-model="activity.form.type" :items="activityTypes" label="Tipo" outlined dense hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                        <v-col cols="12" sm="5"><v-text-field v-model="activity.form.occurred_at" type="date" label="Data" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="12" sm="6"><v-select v-model="activity.form.admin_id" :items="teamItems" label="Responsável" outlined dense hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                        <v-col cols="12" sm="6"><v-text-field v-model="activity.form.with_whom" label="Com quem (juiz, assessor...)" outlined dense hide-details="auto" maxlength="255"></v-text-field></v-col>
                        <v-col cols="12"><v-textarea v-model="activity.form.description" label="Descrição" outlined dense rows="3" auto-grow hide-details="auto"></v-textarea></v-col>
                        <v-col v-if="['meeting', 'court_feedback'].includes(activity.form.type)" cols="12">
                            <v-textarea v-model="activity.form.feedback" label="Feedback do juízo" outlined dense rows="2" auto-grow hide-details="auto"></v-textarea>
                        </v-col>
                        <v-col cols="12" sm="8"><v-text-field v-model="activity.form.next_step" label="Próximo passo (opcional)" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="12" sm="4"><v-text-field v-model="activity.form.next_step_at" type="date" label="Até" outlined dense hide-details="auto"></v-text-field></v-col>
                    </v-row>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="activity.saving" @click="activity.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('save')" color="primary" depressed :loading="activity.saving" :disabled="!activity.form.description || !activity.form.occurred_at" @click="saveActivity">Salvar andamento</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- Dados do caso -->
        <v-dialog v-model="edit.open" max-width="560" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Editar dados do caso</h3>
                        <p class="ui-section-description">Campos editados aqui não são sobrescritos pelas próximas importações.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-text-field v-model="edit.form.company_name" label="Empresa" outlined dense hide-details="auto" class="mb-4" maxlength="500"></v-text-field>
                    <v-row dense>
                        <v-col cols="12" sm="6"><v-text-field v-model="edit.form.debt_amount" label="Passivo declarado (R$)" outlined dense hide-details="auto" inputmode="decimal" placeholder="Ex.: 47117515,24"></v-text-field></v-col>
                        <v-col cols="12" sm="6"><v-select v-model="edit.form.sector" :items="sectorItems" label="Setor" outlined dense clearable hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                        <v-col cols="12" sm="6"><v-select v-model="edit.form.type" :items="typeItems" label="Tipo" outlined dense hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                        <v-col cols="12"><v-textarea v-model="edit.form.authors_lawyers" label="Advogado do devedor" outlined dense rows="2" auto-grow hide-details="auto"></v-textarea></v-col>
                    </v-row>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="edit.saving" @click="edit.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('save')" color="primary" depressed :loading="edit.saving" @click="saveEdit">Salvar</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- Nomeacao -->
        <v-dialog v-model="appointment.open" max-width="560" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">{{ appointment.role === "pp" ? "Perícia prévia (constatação)" : "Administrador judicial" }}</h3>
                        <p class="ui-section-description">{{ appointment.role === "aj" ? "AJ nomeado encerra o caso: Nomeada BeJ ou Nomeado concorrente." : "A PP não encerra o caso; a AJ continua pendente." }}</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <ui-pills v-model="appointment.status" :items="appointmentStatusItems" class="mb-4" />
                    <template v-if="appointment.status === 'appointed'">
                        <v-autocomplete v-model="appointment.administrator_id" :items="administrators" item-value="id" item-text="legal_name" label="Administrador judicial nomeado"
                            outlined dense hide-details="auto" class="mb-2" :filter="administratorFilter" no-data-text="Nenhum AJ encontrado">
                            <template v-slot:item="{ item: option }">
                                <v-list-item-content>
                                    <v-list-item-title>{{ option.legal_name }}<span v-if="option.is_house" class="ui-muted"> · casa</span></v-list-item-title>
                                    <v-list-item-subtitle v-if="option.aliases.length > 1">{{ option.aliases.join(" · ") }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </template>
                        </v-autocomplete>
                        <v-btn v-if="$allowed('create')" x-small text class="mb-4" @click="newAdministrator.open = !newAdministrator.open"><v-icon left x-small>mdi-plus</v-icon>Cadastrar outro AJ</v-btn>
                        <div v-if="newAdministrator.open" class="pd-inline-form mb-4">
                            <v-text-field v-model="newAdministrator.legal_name" label="Razão social / nome" outlined dense hide-details="auto" class="mb-2"></v-text-field>
                            <v-text-field v-model="newAdministrator.document" label="CNPJ ou CPF (opcional)" outlined dense hide-details="auto" class="mb-2"></v-text-field>
                            <v-text-field v-model="newAdministrator.aliases" label="Outras grafias, separadas por ponto e vírgula" outlined dense hide-details="auto" class="mb-2"></v-text-field>
                            <v-btn v-if="$allowed('create')" small color="primary" depressed :loading="newAdministrator.saving" :disabled="!newAdministrator.legal_name" @click="createAdministrator">Cadastrar e selecionar</v-btn>
                        </div>
                        <v-text-field v-model="appointment.appointed_at" type="date" label="Data da nomeação" outlined dense hide-details="auto" class="mb-4"></v-text-field>
                        <v-textarea v-if="appointment.role === 'aj' && selectedAdministrator && !selectedAdministrator.is_house" v-model="appointment.lost_reason"
                            label="Motivo (o que aprendemos com este juízo)" outlined dense rows="2" auto-grow hide-details="auto"></v-textarea>
                    </template>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="appointment.saving" @click="appointment.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('save')" color="primary" depressed :loading="appointment.saving" :disabled="appointment.status === 'appointed' && !appointment.administrator_id" @click="saveAppointment">Salvar</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- Status processual -->
        <v-dialog v-model="procedural.open" max-width="560" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Conferência do status processual</h3>
                        <p class="ui-section-description">Registre o que consta nos autos (Whom ou tribunal). Fica com data e autor e vira andamento dos autos.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-textarea v-model="procedural.text" label="Status conferido" outlined dense rows="3" auto-grow hide-details="auto"
                        placeholder="Ex.: Não há nomeação de AJ, tampouco constatação prévia. Sem RJ distribuída com o mesmo CNPJ."></v-textarea>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="procedural.saving" @click="procedural.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('create')" color="primary" depressed :loading="procedural.saving" :disabled="!procedural.text" @click="saveProcedural">Registrar</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- Contatos da vara -->
        <v-dialog v-model="court.open" max-width="520" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Contatos da vara</h3>
                        <p class="ui-section-description">Os contatos pertencem à vara e aparecem em todos os casos dela.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-text-field v-model="court.form.clerk_name" label="Assessor(a)" outlined dense hide-details="auto" class="mb-3"></v-text-field>
                    <v-text-field v-model="court.form.phone" label="Telefone" outlined dense hide-details="auto" class="mb-3"></v-text-field>
                    <v-text-field v-model="court.form.email" label="E-mail do gabinete" outlined dense hide-details="auto" class="mb-3"></v-text-field>
                    <v-textarea v-model="court.form.notes" label="Observações" outlined dense rows="2" auto-grow hide-details="auto"></v-textarea>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="court.saving" @click="court.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('save')" color="primary" depressed :loading="court.saving" @click="saveCourt">Salvar</v-btn>
                </div>
            </v-card>
        </v-dialog>
        <!-- Prazo da TCA -->
        <v-dialog v-model="deadlineForm.open" max-width="520" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Prazo da TCA</h3>
                        <p class="ui-section-description">30 dias corridos para distribuir a RJ (art. 308 CPC). Com a data da liminar, o prazo deixa de ser estimado.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <ui-pills v-model="deadlineForm.base" :items="baseItems" class="mb-4" />
                    <v-row dense>
                        <template v-if="deadlineForm.base !== 'manual'">
                            <v-col cols="7"><v-text-field v-model="deadlineForm.base_date" type="date" :label="deadlineForm.base === 'liminar' ? 'Data da liminar' : 'Data da distribuição'" outlined dense hide-details="auto"></v-text-field></v-col>
                            <v-col cols="5"><v-text-field v-model="deadlineForm.days" type="number" min="1" label="Dias corridos" outlined dense hide-details="auto"></v-text-field></v-col>
                        </template>
                        <v-col v-else cols="7"><v-text-field v-model="deadlineForm.due_date" type="date" label="Vencimento" outlined dense hide-details="auto"></v-text-field></v-col>
                    </v-row>
                    <p v-if="deadlinePreview" class="ui-small mt-3 mb-0">Vence em <strong>{{ formatFeedDate(deadlinePreview) }}</strong>.</p>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="deadlineForm.saving" @click="deadlineForm.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('save')" color="primary" depressed :loading="deadlineForm.saving" @click="saveDeadline">Salvar</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- Prorrogacao -->
        <v-dialog v-model="extension.open" max-width="480" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Prorrogar prazo</h3>
                        <p class="ui-section-description">A régua recomeça a partir do novo vencimento.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-text-field v-model="extension.due_date" type="date" label="Novo vencimento" outlined dense hide-details="auto" class="mb-3"></v-text-field>
                    <v-text-field v-model="extension.note" label="Fonte (ex.: decisão de 02/09 no Whom)" outlined dense hide-details="auto"></v-text-field>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="extension.saving" @click="extension.open = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :loading="extension.saving" :disabled="!extension.due_date" @click="saveExtension">Prorrogar</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- Desfecho -->
        <v-dialog v-model="outcome.open" max-width="520" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Desfecho da TCA</h3>
                        <p class="ui-section-description">Encerra o prazo e os lembretes. "Sem RJ" move o caso para Encerrado sem RJ.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <ui-pills v-model="outcome.value" :items="outcomeItems" class="mb-4" />
                    <v-text-field v-if="outcome.value === 'rj_distribuida'" v-model="outcome.rj_process_number" label="Número da RJ (opcional)" placeholder="0000000-00.0000.0.00.0000"
                        outlined dense hide-details="auto" class="mb-3"></v-text-field>
                    <v-textarea v-model="outcome.note" label="Observação" outlined dense rows="2" auto-grow hide-details="auto"></v-textarea>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="outcome.saving" @click="outcome.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('create')" color="primary" depressed :loading="outcome.saving" @click="saveOutcome">Registrar</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- Previa do e-mail -->
        <v-dialog v-model="preview.open" max-width="680">
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Prévia do lembrete</h3>
                        <p class="ui-section-description">Como o e-mail do marco sai hoje para este caso.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <ui-pills v-model="preview.milestone" :items="milestoneItems" class="mb-4" />
                    <template v-if="preview.data">
                        <dl class="pd-dl mb-3">
                            <dt>Para</dt><dd>{{ recipientsText(preview.data.recipients) }}</dd>
                            <dt>Assunto</dt><dd>{{ preview.data.subject }}</dd>
                        </dl>
                        <div class="pd-mail">
                            <p>Olá, <span v-html="preview.data.data.name"></span>.</p>
                            <p>O prazo da tutela cautelar de <strong v-html="preview.data.data.company_name"></strong> (<span v-html="preview.data.data.place"></span>, <span v-html="preview.data.data.amount"></span>)
                                vence em <strong>{{ preview.data.data.due_date }}</strong>: <span v-html="preview.data.data.days_text"></span>.</p>
                            <p>Situação atual: <span v-html="preview.data.data.situation"></span></p>
                            <p><strong v-html="preview.data.data.request"></strong></p>
                            <div v-html="preview.data.data.reply_block"></div>
                            <p class="ui-small ui-muted mb-0">{{ preview.data.data.next_text }}</p>
                        </div>
                    </template>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined @click="preview.open = false">Fechar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import ProspectMeetingDialog from "@/components/prospect/ProspectMeetingDialog.vue";
import ProspectTripPicker from "@/components/prospect/ProspectTripPicker.vue";
import {
    ACTIVITY_TONES, ACTIVITY_TYPE_ITEMS, APPOINTMENT_LABELS, MANUAL_STATUS_ITEMS, PIPELINE_LABELS, SECTOR_ITEMS, TYPE_ITEMS,
    formatDate, formatDateTime, formatDocument, formatFeedDate, formatMoney, notifyError, notifySuccess, openUrl, todayKey,
} from "@/components/prospect/prospect.util";

export default {
    name: "ProspectCaseDetails",

    components: { ProspectMeetingDialog, ProspectTripPicker },

    data: () => ({
        item: null,
        trips: [],
        tripPicker: false,
        team: [],
        administrators: [],
        busy: false,
        statusItems: MANUAL_STATUS_ITEMS,
        activityTypes: ACTIVITY_TYPE_ITEMS,
        sectorItems: SECTOR_ITEMS,
        typeItems: TYPE_ITEMS,
        activity: { open: false, saving: false, form: {} },
        edit: { open: false, saving: false, form: {} },
        appointment: { open: false, saving: false, role: "aj", status: "pending", administrator_id: null, appointed_at: "", lost_reason: "" },
        newAdministrator: { open: false, saving: false, legal_name: "", document: "", aliases: "" },
        procedural: { open: false, saving: false, text: "" },
        court: { open: false, saving: false, form: {} },
        meetingDialog: false,
        deadlineForm: { open: false, saving: false, base: "liminar", base_date: "", days: 30, due_date: "" },
        extension: { open: false, saving: false, due_date: "", note: "" },
        outcome: { open: false, saving: false, value: "rj_distribuida", rj_process_number: "", note: "" },
        preview: { open: false, milestone: "d-7", data: null },
        baseItems: [{ value: "liminar", text: "Data da liminar" }, { value: "distribuicao", text: "Distribuição (estimado)" }, { value: "manual", text: "Vencimento" }],
        outcomeItems: [{ value: "rj_distribuida", text: "RJ distribuída" }, { value: "sem_rj", text: "Sem RJ" }],
        milestoneItems: [
            { value: "d-15", text: "D−15" }, { value: "d-7", text: "D−7" }, { value: "d-2", text: "D−2" }, { value: "d0", text: "D0" }, { value: "d+3", text: "D+3" },
        ],
    }),

    computed: {
        // Volta para a aba de Casos (ou para Prazos de TCA) de onde a ficha foi aberta.
        backTo() {
            const from = this.$route.query.from === "pendentes" ? "acompanhamento" : this.$route.query.from;
            if (from === "cautelares") return "/prospeccao/cautelares";
            const tabs = ["a-triar", "acompanhamento", "encerrados", "todos"];
            return tabs.includes(from) ? `/prospeccao/casos?aba=${from}` : "/prospeccao/casos";
        },
        backText() {
            return this.$route.query.from === "cautelares" ? "Prazos de TCA" : "Casos";
        },
        ownerItems() {
            return [{ value: null, text: "Sem dono" }, ...this.team.map((person) => ({ value: person.id, text: person.name }))];
        },
        teamItems() {
            return this.team.map((person) => ({ value: person.id, text: person.name }));
        },
        appointmentStatusItems() {
            const items = [{ value: "pending", text: "Pendente" }, { value: "appointed", text: "Nomeado" }];
            if (this.appointment.role === "pp") items.push({ value: "not_applicable", text: "Não se aplica" });
            return items;
        },
        deadlinePreview() {
            const form = this.deadlineForm;
            if (form.base === "manual") return form.due_date || null;
            if (!form.base_date || !Number(form.days)) return null;
            const date = new Date(`${form.base_date}T12:00:00Z`);
            date.setUTCDate(date.getUTCDate() + Number(form.days));
            return date.toISOString().slice(0, 10);
        },
        selectedAdministrator() {
            return this.administrators.find((option) => option.id === this.appointment.administrator_id) || null;
        },
    },

    watch: {
        "$route.params.id"() {
            this.load();
        },
        "preview.milestone"() {
            if (this.preview.open) this.loadPreview();
        },
    },

    created() {
        this.load();
        this.loadTeam();
    },

    methods: {
        formatDate,
        formatDateTime,
        formatDocument,
        formatFeedDate,
        formatMoney,

        async load() {
            try {
                const result = await axios.get(`/api/admin/prospect/cases/${this.$route.params.id}`);
                this.item = result.data.content;
            } catch (error) {
                notifyError(this, error);
            }
            this.loadTrips();
        },

        async loadTrips() {
            try {
                const result = await axios.get(`/api/admin/prospect/cases/${this.$route.params.id}/trips`);
                this.trips = result.data.content;
            } catch (error) {
                notifyError(this, error);
            }
        },

        async loadTeam() {
            try {
                const result = await axios.get("/api/admin/prospect/team");
                this.team = result.data.content;
            } catch (error) {
                notifyError(this, error);
            }
        },

        async loadAdministrators() {
            try {
                const result = await axios.get("/api/admin/prospect/administrators");
                this.administrators = result.data.content;
            } catch (error) {
                notifyError(this, error);
            }
        },

        tone(type) {
            return ACTIVITY_TONES[type] || "neutral";
        },

        pipelineLabel(status) {
            return PIPELINE_LABELS[status] || status;
        },

        // Andamentos manuais guardam a data de calendario; os automaticos, o instante (UTC).
        formatDay(value) {
            const text = String(value || "");
            if (/ 12:00:00$/.test(text)) return formatFeedDate(text);
            return formatDateTime(text);
        },

        appointmentText(status, administrator) {
            if (status === "appointed") return administrator ? administrator.name : "nomeado";
            return APPOINTMENT_LABELS[status] || status;
        },

        partiesText(parties) {
            return (parties || []).map((party) => (party.document ? `${party.name} (${formatDocument(party.document)})` : party.name)).join("; ");
        },

        administratorFilter(option, queryText) {
            const query = String(queryText || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
            return [option.legal_name, ...(option.aliases || [])].some((name) => String(name).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().includes(query));
        },

        async run(request, message) {
            this.busy = true;
            try {
                const result = await request();
                if (result && result.data && result.data.content && result.data.content.id === this.item.id) this.item = result.data.content;
                else await this.load();
                if (message) notifySuccess(this, message);
                return true;
            } catch (error) {
                notifyError(this, error);
                return false;
            } finally {
                this.busy = false;
            }
        },

        async setOwner(ownerId) {
            await this.run(() => axios.post("/api/admin/prospect/cases/triage", { ids: [this.item.id], owner_admin_id: ownerId }), ownerId ? "Dono atribuído." : "Dono removido.");
        },

        async changeStatus(status) {
            let reason = null;
            if (status === "nao_atuar") {
                reason = await this.$dialog.prompt({ title: 'Optamos por não atuar', text: "Motivo (fica registrado na linha do tempo)", actions: { false: "Cancelar", true: "Registrar" } });
                if (!reason) return;
            }
            await this.run(() => axios.post(`/api/admin/prospect/cases/${this.item.id}/status`, { status, reason }), `Etapa: ${PIPELINE_LABELS[status]}.`);
        },

        openActivity() {
            this.activity = {
                open: true,
                saving: false,
                form: { type: "bej_move", occurred_at: todayKey(), admin_id: this.item.owner ? this.item.owner.id : null, with_whom: "", description: "", feedback: "", next_step: "", next_step_at: "" },
            };
        },

        async saveActivity() {
            this.activity.saving = true;
            try {
                const payload = { ...this.activity.form };
                if (!payload.next_step_at) delete payload.next_step_at;
                await axios.post(`/api/admin/prospect/cases/${this.item.id}/activities`, payload);
                this.activity.open = false;
                notifySuccess(this, "Andamento registrado.");
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.activity.saving = false;
            }
        },

        async removeActivity(activity) {
            const confirmed = await this.$dialog.confirm({
                title: "Excluir andamento",
                text: `"${activity.description}" sai da linha do tempo. Confirma?`,
                actions: { false: "Cancelar", true: { text: "Excluir", color: "error" } },
            });
            if (!confirmed) return;
            await this.run(() => axios.delete(`/api/admin/prospect/activities/${activity.id}`), "Andamento excluído.");
        },

        openEdit() {
            this.edit = {
                open: true,
                saving: false,
                form: {
                    company_name: this.item.company_name,
                    debt_amount: this.item.debt_amount != null ? String(this.item.debt_amount).replace(".", ",") : "",
                    sector: this.item.sector,
                    type: this.item.type,
                    authors_lawyers: this.item.authors_lawyers || "",
                },
            };
        },

        async saveEdit() {
            this.edit.saving = true;
            try {
                const form = this.edit.form;
                const amount = String(form.debt_amount || "").trim();
                const payload = {
                    company_name: form.company_name,
                    debt_amount: amount ? Number(amount.replace(/\./g, "").replace(",", ".")) : null,
                    sector: form.sector || null,
                    type: form.type,
                    authors_lawyers: form.authors_lawyers,
                };
                if (payload.debt_amount !== null && !Number.isFinite(payload.debt_amount)) {
                    this.$dialog.notify.warning("Passivo inválido. Use apenas números, com vírgula nos centavos.", { position: "top-right", timeout: 4000 });
                    return;
                }
                const result = await axios.patch(`/api/admin/prospect/cases/${this.item.id}`, payload);
                this.item = result.data.content;
                this.edit.open = false;
                notifySuccess(this, "Dados atualizados.");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.edit.saving = false;
            }
        },

        async openAppointment(role) {
            const administrator = this.item[`${role}_administrator`];
            this.appointment = {
                open: true,
                saving: false,
                role,
                status: this.item[`${role}_status`],
                administrator_id: administrator ? administrator.id : null,
                appointed_at: this.item[`${role}_appointed_at`] || todayKey(),
                lost_reason: role === "aj" ? this.item.lost_reason || "" : "",
            };
            this.newAdministrator = { open: false, saving: false, legal_name: "", document: "", aliases: "" };
            if (!this.administrators.length) await this.loadAdministrators();
        },

        async createAdministrator() {
            this.newAdministrator.saving = true;
            try {
                const aliases = String(this.newAdministrator.aliases || "").split(";").map((alias) => alias.trim()).filter(Boolean);
                const result = await axios.post("/api/admin/prospect/administrators", { legal_name: this.newAdministrator.legal_name, document: this.newAdministrator.document, aliases });
                await this.loadAdministrators();
                this.appointment.administrator_id = result.data.content.id;
                this.newAdministrator.open = false;
                notifySuccess(this, "AJ cadastrado.");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.newAdministrator.saving = false;
            }
        },

        async saveAppointment() {
            this.appointment.saving = true;
            try {
                const { role, status, administrator_id: administratorId, appointed_at: appointedAt, lost_reason: lostReason } = this.appointment;
                const result = await axios.post(`/api/admin/prospect/cases/${this.item.id}/appointments`, {
                    role, status, administrator_id: status === "appointed" ? administratorId : null, appointed_at: status === "appointed" ? appointedAt : null, lost_reason: lostReason,
                });
                this.item = result.data.content;
                this.appointment.open = false;
                notifySuccess(this, "Nomeação registrada.");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.appointment.saving = false;
            }
        },

        openProcedural() {
            this.procedural = { open: true, saving: false, text: this.item.procedural_status || "" };
        },

        async saveProcedural() {
            this.procedural.saving = true;
            try {
                const result = await axios.post(`/api/admin/prospect/cases/${this.item.id}/procedural-check`, { procedural_status: this.procedural.text });
                this.item = result.data.content;
                this.procedural.open = false;
                notifySuccess(this, "Conferência registrada.");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.procedural.saving = false;
            }
        },

        openCourt() {
            const current = this.item.court || {};
            this.court = { open: true, saving: false, form: { clerk_name: current.clerk_name || "", phone: current.phone || "", email: current.email || "", notes: current.notes || "" } };
        },

        async saveCourt() {
            this.court.saving = true;
            try {
                await axios.patch(`/api/admin/prospect/courts/${this.item.court.id}`, this.court.form);
                this.court.open = false;
                notifySuccess(this, "Contatos da vara atualizados.");
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.court.saving = false;
            }
        },

        openDeadline() {
            const deadline = this.item.deadline;
            this.deadlineForm = {
                open: true,
                saving: false,
                base: deadline && deadline.base !== "distribuicao" ? deadline.base : "liminar",
                base_date: deadline && deadline.base === "liminar" ? deadline.base_date : "",
                days: deadline ? deadline.days : 30,
                due_date: deadline ? deadline.due_date : "",
            };
        },

        async saveDeadline() {
            this.deadlineForm.saving = true;
            try {
                const { base, base_date: baseDate, days, due_date: dueDate } = this.deadlineForm;
                await axios.put(`/api/admin/prospect/cases/${this.item.id}/deadline`, { base, base_date: baseDate, days: Number(days), due_date: dueDate });
                this.deadlineForm.open = false;
                notifySuccess(this, "Prazo atualizado.");
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.deadlineForm.saving = false;
            }
        },

        openExtension() {
            this.extension = { open: true, saving: false, due_date: "", note: "" };
        },

        async saveExtension() {
            this.extension.saving = true;
            try {
                await axios.post(`/api/admin/prospect/cases/${this.item.id}/deadline/extensions`, { due_date: this.extension.due_date, note: this.extension.note });
                this.extension.open = false;
                notifySuccess(this, "Prazo prorrogado.");
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.extension.saving = false;
            }
        },

        openOutcome() {
            this.outcome = { open: true, saving: false, value: "rj_distribuida", rj_process_number: "", note: "" };
        },

        async saveOutcome() {
            this.outcome.saving = true;
            try {
                await axios.post(`/api/admin/prospect/cases/${this.item.id}/deadline/outcome`, {
                    outcome: this.outcome.value,
                    rj_process_number: this.outcome.value === "rj_distribuida" ? this.outcome.rj_process_number : null,
                    note: this.outcome.note,
                });
                this.outcome.open = false;
                notifySuccess(this, "Desfecho registrado.");
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.outcome.saving = false;
            }
        },

        async reopenDeadline() {
            await this.run(() => axios.post(`/api/admin/prospect/cases/${this.item.id}/deadline/outcome`, { outcome: null }), "Prazo reaberto.");
        },

        async togglePause() {
            await this.run(() => axios.post(`/api/admin/prospect/cases/${this.item.id}/deadline/pause`, { paused: !this.item.deadline.reminders_paused }),
                this.item.deadline.reminders_paused ? "Lembretes retomados." : "Lembretes pausados.");
        },

        recipientsText(recipients) {
            if (!recipients || !recipients.length) return "ninguém: cadastre dono, equipe ou gestão";
            return recipients.map((recipient) => (recipient.name ? `${recipient.name} (${recipient.email})` : recipient.email)).join(", ");
        },

        openPreview() {
            this.preview = { open: true, milestone: this.item.deadline.current_milestone || "d-7", data: null };
            this.loadPreview();
        },

        async loadPreview() {
            try {
                const result = await axios.get(`/api/admin/prospect/cases/${this.item.id}/reminder-preview`, { params: { milestone: this.preview.milestone } });
                this.preview.data = result.data.content;
            } catch (error) {
                notifyError(this, error);
            }
        },

        async openFiles() {
            try {
                const result = await axios.get(`/api/admin/prospect/cases/${this.item.id}/files`);
                openUrl(result.data.content.url);
            } catch (error) {
                notifyError(this, error);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/ui/tokens";

.pd-facts {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    border: 1px solid $ui-border;
    border-radius: $ui-radius;
    background: $ui-bg;

    @media (max-width: 960px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

.pd-fact {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    padding: 14px 18px;
    border-right: 1px solid $ui-border;

    &:last-child {
        border-right: 0;
    }
}

.pd-fact__label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    color: $ui-muted;
}

.pd-fact__value {
    font-size: 20px;
    font-weight: 600;
    color: $ui-fg;
}

.pd-fact__value--sm {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    font-size: 15px;
}

.pd-fact__hint {
    font-size: 12px;
    color: $ui-muted;
}

.pd-owner {
    max-width: 220px;
    padding-top: 0;
    margin-top: 0;
}

.pd-link {
    padding: 0;
    border: 0;
    background: none;
    color: $ui-accent;
    font: inherit;
    text-align: left;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
}

.pd-notice {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    font-size: 13px;
    border-radius: $ui-radius;
    background: nth(map-get($ui-tones, info), 1);
    color: nth(map-get($ui-tones, info), 2);
}

.pd-notice--neutral {
    background: nth(map-get($ui-tones, neutral), 1);
    color: nth(map-get($ui-tones, neutral), 2);
}

.pd-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
    gap: 16px;
    align-items: start;

    @media (max-width: 1100px) {
        grid-template-columns: minmax(0, 1fr);
    }
}

// Cartoes lado a lado: sem o espacamento vertical de ".ui-card + .ui-card".
.pd-grid > .ui-card + .ui-card {
    margin-top: 0;
}

.pd-timeline {
    list-style: none;
    margin: 0;
    padding: 0;
}

.pd-event {
    display: grid;
    grid-template-columns: 110px minmax(0, 1fr);
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid $ui-border-soft;

    &:last-child {
        border-bottom: 0;
    }
}

.pd-event--auto .pd-event__text {
    color: $ui-muted;
}

.pd-event__date {
    font-size: 12px;
    color: $ui-muted;
    padding-top: 2px;
}

.pd-event__head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 2px;
}

.pd-event__type {
    padding: 1px 8px;
    font-size: 11px;
    font-weight: 600;
    border-radius: 999px;

    @each $tone, $colors in $ui-tones {
        &--#{$tone} {
            background: nth($colors, 1);
            color: nth($colors, 2);
        }
    }
}

.pd-event__text {
    font-size: 14px;
    white-space: pre-line;
}

.pd-event__feedback {
    margin-top: 4px;
    font-size: 13px;
    color: $ui-text;
}

.pd-dl {
    display: grid;
    grid-template-columns: 130px minmax(0, 1fr);
    gap: 8px 12px;
    margin: 0;
    font-size: 13px;

    dt {
        color: $ui-muted;
    }

    dd {
        margin: 0;
        word-break: break-word;
    }
}

.pd-due {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 12px 14px;
    border-radius: $ui-radius-sm;
    font-size: 13px;

    @each $tone, $colors in $ui-tones {
        &--#{$tone} {
            background: nth($colors, 1);
            color: nth($colors, 2);
        }
    }
}

.pd-due__date {
    font-size: 20px;
    font-weight: 600;
}

.pd-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.pd-mail {
    padding: 16px;
    border: 1px solid $ui-border;
    border-radius: $ui-radius-sm;
    background: $ui-bg-muted;
    font-size: 14px;
}

.pd-trip {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
    font-size: 13px;
    border-bottom: 1px solid $ui-border-soft;

    &:last-child {
        border-bottom: 0;
    }
}

.pd-trip--closed {
    opacity: 0.6;
}

.pd-trip__dot {
    flex: 0 0 10px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
}

.pd-trip__main {
    flex: 1 1 auto;
    min-width: 0;
}

.pd-trip-link {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin-top: 2px;
}

.pd-meetings {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        padding: 6px 8px;
        text-align: left;
        font-weight: 500;
        color: $ui-muted;
        border-bottom: 1px solid $ui-border;
    }

    td {
        padding: 7px 8px;
        border-bottom: 1px solid $ui-border-soft;
        vertical-align: top;
    }
}

.pd-inline-form {
    padding: 12px;
    border: 1px dashed $ui-border-strong;
    border-radius: $ui-radius-sm;
}
</style>
